import { reactive } from 'vue';
import { api } from '@/lib/api';
import { useMeStore } from '@/stores/me';

/**
 * The numbers on the sidebar badges, shared app-wide.
 *
 * Plain module state like the toast queue: one AppShell polls it, and the
 * screens that change a count (fulfilling an order, opening a submission)
 * call refreshNavCounts() so the badge follows at once instead of on the
 * next tick.
 *
 *   ordersToShip           paid orders not yet shipped (paid, processing,
 *                          packing); null when the checkout module is off
 *   formSubmissionsUnread  submissions nobody has opened yet (spam excluded)
 */
interface NavCountsState {
    ordersToShip: number | null;
    formSubmissionsUnread: number;
}

interface NavCountsResponse {
    orders_to_ship: number | null;
    form_submissions_unread: number;
}

const state = reactive<NavCountsState>({ ordersToShip: null, formSubmissionsUnread: 0 });

export function useNavCounts(): Readonly<NavCountsState> {
    return state;
}

/** Forget the counts, e.g. on a website switch, so no badge shows another tenant's number. */
export function resetNavCounts(): void {
    state.ordersToShip = null;
    state.formSubmissionsUnread = 0;
}

/** Silent on failure: a badge that stays put is better than an error toast every 20s. */
export async function refreshNavCounts(): Promise<void> {
    const me = useMeStore();
    const websiteId = me.me?.activeWebsite?.id;
    if (!websiteId) return;
    try {
        const r = await api<NavCountsResponse>(`/api/admin/websites/${websiteId}/nav-counts`);
        // The user may have switched website while this was in flight.
        if (me.me?.activeWebsite?.id !== websiteId) return;
        state.ordersToShip = r.orders_to_ship;
        state.formSubmissionsUnread = r.form_submissions_unread;
    } catch {
        // Silent
    }
}
